import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useFieldState } from '../../lib/fieldState';
import { FieldStatusIcon } from '../ui/Visuals';

/** Right-hand side sheet built on the Radix dialog (focus trap, Esc to close, restores focus). */
export function Sheet({ title, subtitle, onClose, onCloseFocus, children, footer }: { title: string; subtitle?: string; onClose: () => void; onCloseFocus?: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay" />
        <Dialog.Content
          className="sheet"
          aria-describedby={undefined}
          data-testid="drawer"
          onCloseAutoFocus={(e) => {
            if (onCloseFocus) {
              e.preventDefault();
              onCloseFocus();
            }
          }}
        >
          <div className="flex items-start gap-2 border-b border-line px-4 py-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="truncate text-[17px] font-semibold">{title}</Dialog.Title>
              {subtitle ? <p className="text-[13.5px] text-faint">{subtitle}</p> : null}
            </div>
            <Dialog.Close asChild>
              <button type="button" className="btn btn-ghost btn-icon" aria-label="Close">
                <X className="size-4" />
              </button>
            </Dialog.Close>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">{children}</div>
          {footer ? <div className="border-t border-line px-4 py-2.5 text-[13.5px] text-dim">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Field({ label, note, children, htmlFor }: { label: string; note?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="space-y-1">
      <label className="field-label block" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {note ? <p className="text-[13px] text-faint">{note}</p> : null}
    </div>
  );
}

interface TextProps {
  label: string;
  value: string;
  stateKey: string;
  onCommit: (value: string) => void;
  disabled?: boolean;
  compact?: boolean;
}

/** Text input saved on blur / Enter (Esc reverts). Shows saving state and the inline error. */
export function TextCommitField({ label, value, stateKey, onCommit, disabled, compact }: TextProps) {
  const id = useId();
  const state = useFieldState(stateKey);
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  const commit = () => {
    const next = draft.trim();
    if (!next) {
      setDraft(value);
      return;
    }
    if (next !== value) onCommit(next);
  };
  const input = (
    <div className="flex items-center gap-2">
      <input
        id={id}
        className={`input ${compact ? '!h-7' : '!h-8'} ${state?.status === 'saved' ? 'fs-saved' : ''}`}
        aria-label={compact ? label : undefined}
        aria-invalid={state?.status === 'error' || undefined}
        value={draft}
        maxLength={255}
        disabled={disabled || state?.status === 'saving'}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            e.stopPropagation();
            setDraft(value);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      <FieldStatusIcon state={state} />
    </div>
  );
  return compact ? (
    input
  ) : (
    <Field label={label} htmlFor={id}>
      {input}
      {state?.status === 'error' ? (
        <p className="text-[13.5px] text-danger" role="alert">
          {state.message}
        </p>
      ) : null}
    </Field>
  );
}

export function FieldError({ stateKey }: { stateKey: string }) {
  const state = useFieldState(stateKey);
  return state?.status === 'error' ? (
    <p className="text-[13.5px] text-danger" role="alert">
      {state.message}
    </p>
  ) : null;
}
